import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import axios from "axios";

export default function MarketplaceActionModal({ show, onHide, selectedData, onSuccess, activeTab }) {
    const [view, setView] = useState("selection");
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState("");

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        city: "",
        address: "",
        payment_date: "",
        payment_message: "",
        image_1: null,
        image_2: null,
    });

    // Sub multi-marketplace mein check karein agar at least 1 sold/hidden hai
    const isAnySold =
        selectedData?.hasAnySoldMarketplace ||
        selectedData?.marketplace?.showing === false ||
        selectedData?.marketplace?.showing === 0 ||
        selectedData?.marketplace?.status === "Sold" ||
        activeTab === "sold";

    useEffect(() => {
        if (!show) {
            setView("selection");
            setErrors({});
            setGeneralError("");
            setFormData({
                name: "",
                email: "",
                phone: "",
                country: "",
                city: "",
                address: "",
                payment_date: "",
                payment_message: "",
                image_1: null,
                image_2: null,
            });
        }
    }, [show]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        setFormData((prev) => ({ ...prev, [name]: files[0] || null }));
    };

    const handleMarkSold = async () => {
        setLoading(true);
        setErrors({});
        setGeneralError("");
        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/market-placer/product/mark-status`,
                {
                    id: selectedData?.marketplace?.id,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            if (response.data.success) {
                onSuccess();
                onHide();
            } else {
                setGeneralError(response.data.message || "Failed to update status.");
            }
        } catch (err) {
            if (err.response && err.response.data) {
                if (err.response.data.errors) {
                    setErrors(err.response.data.errors);
                }
                setGeneralError(err.response.data.message || "An error occurred while updating status.");
            } else {
                setGeneralError("Server error. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});
        setGeneralError("");
        const token = localStorage.getItem("authToken");

        const payload = new FormData();
        payload.append("product_marketplace_id", selectedData?.marketplace?.id || "");
        payload.append("product_id", selectedData?.product?.id || "");
        payload.append("name", formData.name);
        payload.append("email", formData.email);
        payload.append("phone", formData.phone);
        payload.append("country", formData.country);
        payload.append("city", formData.city);
        payload.append("address", formData.address);
        payload.append("payment_date", formData.payment_date);

        if (formData.payment_message) {
            payload.append("payment_message", formData.payment_message);
        }
        if (formData.image_1) {
            payload.append("image_1", formData.image_1);
        }
        if (formData.image_2) {
            payload.append("image_2", formData.image_2);
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/market-placer/product/punch-order`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            if (response.data.success) {
                onSuccess();
                onHide();
            } else {
                setGeneralError(response.data.message || "Action failed. Please try again.");
            }
        } catch (err) {
            if (err.response && err.response.data) {
                if (err.response.data.errors) {
                    setErrors(err.response.data.errors);
                }
                setGeneralError(err.response.data.message || "Validation failed or server error.");
            } else {
                setGeneralError("Server error. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const renderMarketplaceLink = () => {
        const mp = selectedData?.marketplace;
        if (!mp) return null;

        let fullUrl = mp.reference || "";
        if (mp.type === "id" && mp.base_url) {
            fullUrl = `${mp.base_url}${mp.reference || ""}`;
        }

        if (!fullUrl) return null;

        return (
            <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-decoration-underline small fw-normal d-inline-block mt-1"
            >
                {fullUrl}
            </a>
        );
    };

    const productImage =
        selectedData?.product?.card_img ||
        selectedData?.product?.first_img;

    const fullImageUrl = productImage ? process.env.REACT_APP_IMG_URL + productImage : null;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered className="marketplace-modal">
            <Modal.Header closeButton className="px-4 pt-4 pb-3 border-bottom">
                <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                    <div>
                        <Modal.Title className="fw-bold mb-0 fs-5">
                            {selectedData?.marketplace?.name || "Marketplace"} Options
                        </Modal.Title>
                        {renderMarketplaceLink()}
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="p-4">
                {generalError && (
                    <div className="alert alert-danger mb-4 shadow-sm" role="alert">
                        {generalError}
                    </div>
                )}

                <div className="card border-0 bg-light rounded-3 p-3 mb-4 shadow-sm">
                    <div className="d-flex align-items-center gap-3">
                        {fullImageUrl ? (
                            <img
                                src={fullImageUrl}
                                alt={selectedData?.product?.title || "Product Image"}
                                className="rounded border bg-white object-fit-cover shadow-sm"
                                style={{ width: "70px", height: "70px" }}
                            />
                        ) : (
                            <div
                                className="rounded border bg-white d-flex align-items-center justify-content-center text-muted shadow-sm"
                                style={{ width: "70px", height: "70px", fontSize: "12px" }}
                            >
                                No Image
                            </div>
                        )}
                        <div>
                            <span className="badge bg-secondary mb-1">
                                Code: {selectedData?.product?.product_code || "N/A"}
                            </span>
                            <h5 className="fw-bold text-dark mb-1">
                                {selectedData?.product?.title || "Product Title"}
                            </h5>
                            <div className="text-muted small">
                                {selectedData?.product?.selling_style && (
                                    <span className="me-3">
                                        Style: <strong>{selectedData?.product?.selling_style}</strong>
                                    </span>
                                )}
                                {selectedData?.product?.sell_price_dollar !== undefined && (
                                    <span>
                                        Price: <strong>${selectedData?.product?.sell_price_dollar}</strong>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {view === "selection" ? (
                    <div className="d-flex justify-content-center align-items-center gap-4 py-4">
                        <button
                            className="btn btn-danger btn-lg px-5 py-3 rounded-3 shadow-sm fw-bold"
                            onClick={handleMarkSold}
                            disabled={loading}
                        >
                            {loading ? "Processing..." : "Mark Status"}
                        </button>

                        {/* Punch Order Button is rendered ONLY when NONE of the marketplaces are sold */}
                        {!isAnySold && (
                            <button
                                className="btn btn-primary btn-lg px-5 py-3 rounded-3 shadow-sm fw-bold"
                                onClick={() => setView("form")}
                                disabled={loading}
                            >
                                Punch Order
                            </button>
                        )}
                    </div>
                ) : (
                    <form onSubmit={handleFormSubmit}>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Enter full name"
                                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                />
                                {errors.name && (
                                    <div className="invalid-feedback">{errors.name[0]}</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Email <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="example@domain.com"
                                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                />
                                {errors.email && (
                                    <div className="invalid-feedback">{errors.email[0]}</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Phone <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="phone"
                                    placeholder="+1234567890"
                                    className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                />
                                {errors.phone && (
                                    <div className="invalid-feedback">{errors.phone[0]}</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Country <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="country"
                                    placeholder="Enter country"
                                    className={`form-control ${errors.country ? "is-invalid" : ""}`}
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    required
                                />
                                {errors.country && (
                                    <div className="invalid-feedback">{errors.country[0]}</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    City <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="Enter city"
                                    className={`form-control ${errors.city ? "is-invalid" : ""}`}
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    required
                                />
                                {errors.city && (
                                    <div className="invalid-feedback">{errors.city[0]}</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Payment Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="payment_date"
                                    className={`form-control ${errors.payment_date ? "is-invalid" : ""}`}
                                    value={formData.payment_date}
                                    onChange={handleInputChange}
                                    required
                                />
                                {errors.payment_date && (
                                    <div className="invalid-feedback">{errors.payment_date[0]}</div>
                                )}
                            </div>

                            <div className="col-md-12">
                                <label className="form-label fw-semibold">
                                    Address <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    name="address"
                                    placeholder="Enter complete delivery address"
                                    className={`form-control ${errors.address ? "is-invalid" : ""}`}
                                    rows="2"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    required
                                ></textarea>
                                {errors.address && (
                                    <div className="invalid-feedback">{errors.address[0]}</div>
                                )}
                            </div>

                            <div className="col-md-12">
                                <label className="form-label fw-semibold">
                                    Payment Message <span className="text-muted fw-normal">(Optional)</span>
                                </label>
                                <textarea
                                    name="payment_message"
                                    placeholder="Enter payment confirmation or reference details"
                                    className={`form-control ${errors.payment_message ? "is-invalid" : ""}`}
                                    rows="2"
                                    value={formData.payment_message}
                                    onChange={handleInputChange}
                                ></textarea>
                                {errors.payment_message && (
                                    <div className="invalid-feedback">{errors.payment_message[0]}</div>
                                )}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Image 1 <span className="text-muted fw-normal">(Optional)</span>
                                </label>
                                <input
                                    type="file"
                                    name="image_1"
                                    className={`form-control ${errors.image_1 ? "is-invalid" : ""}`}
                                    accept="image/jpg,image/jpeg,image/png,image/webp"
                                    onChange={handleFileChange}
                                />
                                {errors.image_1 && (
                                    <div className="invalid-feedback">{errors.image_1[0]}</div>
                                )}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Image 2 <span className="text-muted fw-normal">(Optional)</span>
                                </label>
                                <input
                                    type="file"
                                    name="image_2"
                                    className={`form-control ${errors.image_2 ? "is-invalid" : ""}`}
                                    accept="image/jpg,image/jpeg,image/png,image/webp"
                                    onChange={handleFileChange}
                                />
                                {errors.image_2 && (
                                    <div className="invalid-feedback">{errors.image_2[0]}</div>
                                )}
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                            <button
                                type="button"
                                className="btn btn-secondary px-4"
                                onClick={() => setView("selection")}
                                disabled={loading}
                            >
                                Back
                            </button>
                            <button type="submit" className="btn btn-success px-4" disabled={loading}>
                                {loading ? "Submitting..." : "Submit Order"}
                            </button>
                        </div>
                    </form>
                )}
            </Modal.Body>
        </Modal>
    );
}